import React, { useMemo, useCallback, useReducer } from 'react'
import * as R from 'ramda'

/**
 * @template S
 * @template A
 * @callback Reducer
 * @param {S} state
 * @param {A} action
 */

/**
 * @template S
 * @callback SetContext
 * @param {S} value
 */

/**
 * @template A
 * @callback Dispatch
 * @param {A} action
 */

/**
 * @template S
 * @template A
 * @typedef ContextValue
 * @property {SetContext<Partial<S>>} setContext
 * @property {Dispatch<A>} dispatch
 */

/**
 *
 * @template S
 * @template A
 * @param {S} [defaultValue]
 * @param {Reducer<S, A>=} reducer
 */
// @ts-ignore
export const createContext = (defaultValue = {}, reducer) => {
  /**
   * @type {React.Context<S & ContextValue<S, A>>}
   */

  const context = React.createContext(null)

  function Provider({ value = {}, children }) {
    const [state, dispatch] = useReducer(reducer || defaultReducer, defaultValue)

    const dispatchContext = useCallback((action) => {
      if (typeof reducer !== 'function') {
        throw Error(`reducer must be a function, but got ${typeof reducer}`)
      }

      return dispatch(action)
    }, [])

    const setContext = useCallback((value) => {
      // @ts-ignore
      dispatch({ type: 'SET_CONTEXT', data: value })
    }, [])

    const contextValue = useMemo(
      /**
       * @returns {S & ContextValue<S, A>}
       */
      () => {
        return {
          ...state,
          ...value,
          dispatch: dispatchContext,
          setContext,
        }
      },
      [dispatchContext, setContext, state, value]
    )

    return <context.Provider value={contextValue}>{children}</context.Provider>
  }

  const { Consumer } = context

  const withContext = (Component) => (props) =>
    <Consumer>{(context) => <Component context={context} {...props} />}</Consumer>

  return {
    Provider,
    Consumer,
    withContext,
    context,
  }
}

function defaultReducer(state, action) {
  if (action.type === 'SET_CONTEXT') {
    return R.mergeDeepRight(state, action.data)
  }

  return state
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/libs/createContext/index.js